import type { Esito, TipoEnte, Valutazione } from "@app/schema";
import { eAlbo, oggettoGara, type Gara, type GareRepos, type Lotto, type Servizio } from "./gare";

/**
 * Report: riepilogo degli esiti ed estrazione filtrata.
 *
 * Unità di conteggio degli esiti: la "partecipazione", cioè la gara senza lotti
 * oppure il singolo lotto (l'esito e l'importo aggiudicato stanno sul lotto).
 * Le gare si contano a parte, una volta sola anche se hanno più lotti.
 * Il periodo si applica alla data di scadenza della gara.
 */

export type EsitoReport = Esito | "In attesa";
export const ESITI_REPORT: readonly EsitoReport[] = ["Aggiudicata", "Non aggiudicata", "In attesa"];

export interface Riga {
  gara: Gara;
  lotto?: Lotto;
  codice: string;
  lottoNumero?: number;
  oggetto: string;
  stazione: string;
  tipoEnte?: TipoEnte;
  servizio?: Servizio;
  scadenza?: string;
  importo?: number;
  valutazione: Valutazione;
  /** Solo per le partecipazioni (valutazione "Partecipa"). */
  esito?: EsitoReport;
  importoAggiudicato?: number;
  cartellaUrl?: string;
}

export interface FiltriReport {
  /** gg inclusi, formato aaaa-mm-gg (data della scadenza nel fuso italiano). */
  dal?: string;
  al?: string;
  servizio?: number;
  tipoEnte?: TipoEnte;
  valutazione?: Valutazione;
  esito?: EsitoReport;
}

/** Tutte le gare (in corso e archiviate), tutti i lotti e i servizi. */
export async function loadDatiReport(r: GareRepos): Promise<{ gare: Gara[]; lotti: Lotto[]; servizi: Servizio[] }> {
  const [attive, archiviate, lotti, servizi] = await Promise.all([
    r.gare.queryAll({ filter: "fields/Stato eq 'Attiva'", top: 500, allowNonIndexed: true }),
    r.gare.queryAll({ filter: "fields/Stato eq 'Archiviata'", top: 500, allowNonIndexed: true }),
    r.lotti.queryAll({ top: 500 }),
    r.servizi.queryAll({ top: 500 }),
  ]);
  // Gli albi non sono gare: restano fuori dai report
  return { gare: [...attive, ...archiviate].filter((g) => !eAlbo(g)), lotti, servizi };
}

/** Una riga per gara senza lotti, una per lotto nelle gare a lotti. */
export function righeReport(gare: Gara[], lotti: Lotto[], servizi: Servizio[]): Riga[] {
  const serv = new Map(servizi.map((s) => [s.id, s]));
  const perGara = new Map<number, Lotto[]>();
  for (const l of lotti) if (l.fields.Gara) perGara.set(l.fields.Gara, [...(perGara.get(l.fields.Gara) ?? []), l]);

  const out: Riga[] = [];
  for (const g of gare) {
    const f = g.fields;
    const valutazione = (f.Valutazione ?? "Da valutare") as Valutazione;
    const base = {
      gara: g,
      codice: f.Codice ?? "",
      stazione: f.Stazione ?? "",
      tipoEnte: f.TipoEnte as TipoEnte | undefined,
      servizio: serv.get(f.Servizio ?? 0),
      scadenza: f.Scadenza,
      valutazione,
    };
    const ls = f.PerLotti ? (perGara.get(g.id) ?? []).sort((a, b) => (a.fields.Numero ?? 0) - (b.fields.Numero ?? 0)) : [];
    if (ls.length) {
      for (const l of ls)
        out.push({
          ...base,
          lotto: l,
          lottoNumero: l.fields.Numero,
          oggetto: l.fields.Title ? `${oggettoGara(g)} – ${l.fields.Title}` : oggettoGara(g),
          importo: l.fields.Importo,
          esito: valutazione === "Partecipa" ? ((l.fields.Esito as Esito | undefined) ?? "In attesa") : undefined,
          importoAggiudicato: l.fields.Esito === "Aggiudicata" ? l.fields.ImportoAggiudicato : undefined,
          cartellaUrl: l.fields.CartellaUrl,
        });
    } else {
      out.push({
        ...base,
        oggetto: oggettoGara(g),
        importo: f.Importo,
        esito: valutazione === "Partecipa" ? ((f.Esito as Esito | undefined) ?? "In attesa") : undefined,
        importoAggiudicato: f.Esito === "Aggiudicata" ? f.ImportoAggiudicato : undefined,
        cartellaUrl: f.CartellaUrl,
      });
    }
  }
  return out;
}

const giornoRoma = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" });
/** ISO → aaaa-mm-gg nel fuso italiano. */
export const giorno = (iso: string) => giornoRoma.format(new Date(iso));

export function filtraRighe(righe: Riga[], f: FiltriReport): Riga[] {
  return righe.filter((r) => {
    if (f.dal || f.al) {
      if (!r.scadenza) return false;
      const d = giorno(r.scadenza);
      if (f.dal && d < f.dal) return false;
      if (f.al && d > f.al) return false;
    }
    if (f.servizio && r.servizio?.id !== f.servizio) return false;
    if (f.tipoEnte && r.tipoEnte !== f.tipoEnte) return false;
    if (f.valutazione && r.valutazione !== f.valutazione) return false;
    if (f.esito && r.esito !== f.esito) return false;
    return true;
  });
}

export interface Riepilogo {
  gare: number;
  partecipate: number;
  nonPartecipate: number;
  daValutare: number;
  /** Gare senza lotti + lotti, delle gare a cui si partecipa. */
  partecipazioni: number;
  aggiudicate: number;
  nonAggiudicate: number;
  inAttesa: number;
  /** Aggiudicate / esiti noti (undefined se nessun esito noto). */
  tasso?: number;
  importoPartecipato: number;
  importoAggiudicato: number;
}

export function riepilogo(righe: Riga[]): Riepilogo {
  const gare = new Map<number, Valutazione>();
  for (const r of righe) gare.set(r.gara.id, r.valutazione);
  const part = righe.filter((r) => r.valutazione === "Partecipa");
  const aggiudicate = part.filter((r) => r.esito === "Aggiudicata").length;
  const nonAggiudicate = part.filter((r) => r.esito === "Non aggiudicata").length;
  const somma = (xs: Array<number | undefined>) => Math.round(xs.reduce<number>((t, x) => t + (x ?? 0), 0) * 100) / 100;
  const v = [...gare.values()];
  return {
    gare: gare.size,
    partecipate: v.filter((x) => x === "Partecipa").length,
    nonPartecipate: v.filter((x) => x === "Non partecipa").length,
    daValutare: v.filter((x) => x === "Da valutare").length,
    partecipazioni: part.length,
    aggiudicate,
    nonAggiudicate,
    inAttesa: part.filter((r) => r.esito === "In attesa").length,
    tasso: aggiudicate + nonAggiudicate ? aggiudicate / (aggiudicate + nonAggiudicate) : undefined,
    importoPartecipato: somma(part.map((r) => r.importo)),
    importoAggiudicato: somma(part.map((r) => r.importoAggiudicato)),
  };
}

export type Raggruppamento = "servizio" | "tipoEnte" | "anno";

export function raggruppa(righe: Riga[], per: Raggruppamento): Array<{ chiave: string; riepilogo: Riepilogo }> {
  const chiave = (r: Riga) =>
    per === "servizio"
      ? r.servizio
        ? `${r.servizio.fields.Sigla} – ${r.servizio.fields.Title}`
        : "Senza servizio"
      : per === "tipoEnte"
        ? (r.tipoEnte ?? "Non indicato")
        : r.scadenza
          ? giorno(r.scadenza).slice(0, 4)
          : "Senza scadenza";
  const gruppi = new Map<string, Riga[]>();
  for (const r of righe) gruppi.set(chiave(r), [...(gruppi.get(chiave(r)) ?? []), r]);
  return [...gruppi.entries()]
    .map(([k, rs]) => ({ chiave: k, riepilogo: riepilogo(rs) }))
    .sort((a, b) => (per === "anno" ? b.chiave.localeCompare(a.chiave) : a.chiave.localeCompare(b.chiave, "it")));
}
